<script lang="ts">
import type {PropType} from "vue";

import {defineComponent} from "vue";
import LoadingIcon from "@/assets/icons/LoadingIcon.vue";
import ArrowDownIcon from "@/assets/icons/ArrowDownIcon.vue";
import {LoadingType} from "@/type/LoadingType";

export default defineComponent({
  name: 'Button',
  components: {ArrowDownIcon, LoadingIcon},
  props: {
    link: {
      type: String,
      default: ''
    },

    buttonClass: {
      type: String,
      default: ''
    },

    type: {
      type: String,
      default: 'default',
      validator: value => ['default', 'yellow', 'red', 'orange', 'green', 'blue', 'gray'].includes(value as string)
    },

    disabled: {
      type: Boolean,
      default: false
    },

    callback: {
      type: Function as PropType<(value?: undefined) => unknown>,
      required: false
    },
  },

  data() {
    return {
      loading: false,
    }
  },

  computed: {
    LoadingType() {
      return LoadingType
    },
    isActive() {
      return this.$route.path === this.link ? '-active' : '';
    }
  },

  methods: {
    async startAsyncTask() {
      if (this.loading || this.disabled) return;

      this.loading = true

      try {
        await this.callback!()
      } catch (error) {
        this.loading = false
      } finally {
        this.loading = false
      }
    }
  }

});
</script>

<template>
  <div>
    <div :data-button="`${type + isActive}`" class="d-flex flex-column gap-0 button-container align-items-center"
         :class="loading || disabled ? 'disabled' : ''" @click="startAsyncTask">
      <router-link class="button d-flex align-content-center justify-content-center align-items-center gap-2 w-100 text-center"
                   :class="[buttonClass]" :to="link">
        <template v-if="loading">
          <LoadingIcon :type="LoadingType.SMALL"/>
        </template>
        <template v-else>
          <slot/>
        </template>
      </router-link>

      <ArrowDownIcon v-if="isActive" class="arrow"/>
    </div>
  </div>
</template>

<style lang="scss">
[data-button="default"] {
  --button-bg: linear-gradient(0deg, rgba(68, 71, 72, 0.5) 0%, rgba(68, 71, 72, 0.375) 100%);
  --button-border: linear-gradient(0deg, rgba(68, 71, 72, 0.1) 0%, rgba(68, 71, 72, 0.025) 100%);
  --button-icon: invert(100%) sepia(4%) saturate(464%) hue-rotate(136deg) brightness(88%) contrast(77%);
  --button: rgba(255, 255, 255, 0.75);
  --button-bg-hover: linear-gradient(0deg, rgba(68, 71, 72, 0.5) 0%, rgba(68, 71, 72, 0.375) 100%);
  --button-border-hover: linear-gradient(0deg, rgba(68, 71, 72, 0.1) 0%, rgba(68, 71, 72, 0.025) 100%);
}

[data-button="yellow"] {
  --button-bg: linear-gradient(0deg, rgba(114, 104, 14, 1) 0%, rgba(64, 59, 19, 1) 100%);
  --button-border: linear-gradient(0deg, rgba(173, 163, 72, 1) 0%, rgba(86, 79, 26, 1) 100%);
  --button-icon: invert(91%) sepia(22%) saturate(538%) hue-rotate(4deg) brightness(99%) contrast(87%);
  --button: rgb(232, 225, 161);
  --button-bg-hover: linear-gradient(0deg, rgba(158, 145, 26, 1) 0%, rgba(94, 86, 21, 1) 100%);
  --button-border-hover: linear-gradient(0deg, rgba(208, 196, 86, 1) 0%, rgba(130, 120, 39, 1) 100%);
}

[data-button="red"] {
  --button-bg: linear-gradient(0deg, rgba(127, 67, 67, 1) 0%, rgba(69, 22, 22, 1) 100%);
  --button-border: linear-gradient(0deg, rgba(187, 102, 102, 1) 0%, rgba(92, 36, 36, 1) 100%);
  --button-icon: invert(77%) sepia(0%) saturate(47%) hue-rotate(136deg) brightness(99%) contrast(93%);
  --button: rgb(255, 184, 184);
  --button-bg-hover: linear-gradient(0deg, rgba(198, 95, 95, 1) 0%, rgba(99, 47, 47, 1) 100%);
  --button-border-hover: linear-gradient(0deg, rgba(235, 159, 159, 1) 0%, rgba(161, 76, 76, 1) 100%);
}

[data-button="orange"] {
  --button-bg: linear-gradient(0deg, rgba(116, 72, 40, 1) 0%, rgba(68, 48, 24, 1) 100%);
  --button-border: linear-gradient(0deg, rgba(155, 102, 64, 1) 0%, rgba(77, 61, 33, 1) 100%);
  --button-icon: invert(86%) sepia(23%) saturate(538%) hue-rotate(335deg) brightness(104%) contrast(106%);
  --button: rgb(255, 231, 184);
  --button-bg-hover: linear-gradient(0deg, rgba(186, 114, 61, 1) 0%, rgba(98, 68, 33, 1) 100%);
  --button-border-hover: linear-gradient(0deg, rgba(212, 151, 107, 1) 0%, rgba(123, 86, 45, 1) 100%);
}

[data-button="green"] {
  --button-bg: linear-gradient(0deg, rgba(64, 132, 79, 1) 0%, rgba(28, 68, 37, 1) 100%);
  --button-border: linear-gradient(0deg, rgba(100, 181, 117, 1) 0%, rgba(36, 81, 47, 1) 100%);
  --button-icon: invert(93%) sepia(10%) saturate(1130%) hue-rotate(72deg) brightness(102%) contrast(102%);
  --button: rgb(184, 255, 208);
  --button-bg-hover: linear-gradient(0deg, rgba(80, 171, 100, 1) 0%, rgba(43, 108, 58, 1) 100%);
  --button-border-hover: linear-gradient(0deg, rgba(128, 232, 150, 1) 0%, rgba(56, 125, 72, 1) 100%);
}

[data-button="blue"] {
  --button-bg: linear-gradient(0deg, rgba(52, 97, 114, 1) 0%, rgba(27, 45, 53, 1) 100%);
  --button-border: linear-gradient(0deg, rgba(78, 123, 142, 1) 0%, rgba(58, 82, 93, 1) 100%);
  --button-icon: invert(86%) sepia(11%) saturate(1803%) hue-rotate(176deg) brightness(104%) contrast(101%);
  --button: rgba(184, 232, 255, 1);
  --button-bg-hover: linear-gradient(0deg, rgba(73, 127, 148, 1) 0%, rgba(42, 72, 85, 1) 100%);
  --button-border-hover: linear-gradient(0deg, rgba(111, 178, 206, 1) 0%, rgba(84, 118, 134, 1) 100%);
}

[data-button="gray"] {
  --button-bg: linear-gradient(0deg, rgba(111, 111, 111, 1) 0%, rgba(58, 58, 58, 1) 100%);
  --button-border: linear-gradient(0deg, rgba(155, 155, 155, 1) 0%, rgba(68, 68, 68, 1) 100%);
  --button-icon: invert(77%) sepia(0%) saturate(47%) hue-rotate(136deg) brightness(99%) contrast(93%);
  --button: rgba(255, 255, 255, 0.75);
  --button-bg-hover: linear-gradient(0deg, rgba(151, 151, 151, 1) 0%, rgba(72, 72, 72, 1) 100%);
  --button-border-hover: linear-gradient(0deg, rgba(211, 211, 211, 1) 0%, rgba(118, 118, 118, 1) 100%);
}

[data-button="blue-active"] {
  --button-bg: linear-gradient(0deg, rgba(184, 232, 255, 0.25) 0%, rgba(184, 232, 255, 0.129)) 100%;
  --button-border: linear-gradient(0deg, rgba(184, 232, 255, 1) 0%, rgba(184, 232, 255, 0.5) 100%);
  --button-icon: invert(80%) sepia(25%) saturate(367%) hue-rotate(165deg) brightness(103%) contrast(103%);
  --button: rgba(184, 232, 255, 1);
  --button-bg-hover: linear-gradient(0deg, rgba(184, 232, 255, 0.25) 0%, rgba(184, 232, 255, 0.129)) 100%;
  --button-border-hover: linear-gradient(0deg, rgba(184, 232, 255, 1) 0%, rgba(184, 232, 255, 0.5) 100%);
}

.button-container {
  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  .button {
    position: relative;

    min-height: 35px;

    box-shadow: 0 2px 7px -2px rgba(10, 17, 21, 0.25);

    cursor: pointer;
    z-index: 1;

    font-family: "Artifex CF Demi Bold", serif;
    font-size: 14px;

    color: var(--button);

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    svg {
      filter: var(--button-icon);
    }

    &::before, &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;

      border-radius: 2px;

      transition: all 0.3s ease;
    }

    &::before {
      background: var(--button-bg);

      z-index: -2;
    }

    &::after {
      padding: 1px;

      background: var(--button-border);
      mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
      -webkit-mask-composite: destination-out;
      mask-composite: exclude;

      z-index: -1;
    }

    &:hover::before {
      background: var(--button-bg-hover);
    }

    &:hover::after {
      background: var(--button-border-hover);
    }
  }

  .arrow {
    height: 5px;
    filter: var(--button-icon);
  }
}
</style>
